import { Box, SelectChangeEvent } from '@mui/material';
import { useEffect, useState, useRef, useCallback } from 'react';
import Paper, { PaperProps } from '../components/paper/Paper';
import { GetAllUserCollections, GetInteractionsData } from '../services/DigestService';
import { filterBarStyle } from '../components/filterbar/FilterBar';
import JumpAndCollapsePanel from '../components/JumpAndCollapsePanel';
import { jumpDown, jumpUp } from '../components/JumpAndCollapsePanel';
import MobileSwiper from '../utils/mobileSwiper';
import FilterBarSortParam from '../components/filterbar/FilterBarSortParam';
import FilterBarShowParam from '../components/filterbar/FilterBarShowParam';
import BoxDefaultContent from '../components/box/BoxDefaultContent';
import responseSetPaperProps from '../utils/setPaperPropsUtils';
import InfiniteScroll from 'react-infinite-scroll-component';
import TripleDotLoadingProgress from '../components/tools/TripleDotLoadingProgress';
import { FeatureToggle, extractFeatureToggles } from './settings/FeatureToggleSettingsPage';
import { GetFeatureToggles } from '../services/SettingsService';
import { useNavigate } from 'react-router-dom';
import CompactPaper, { CompactPaperProps } from '../components/paper/CompactPaper';

type InteractionsPageProps = {
  filterBarHeight?: number
}

export default function InteractionsPage({ filterBarHeight }: InteractionsPageProps) {
  const navigate = useNavigate();

  const [showParam, setShowParam] = useState('all');
  const [sortParam, setSortParam] = useState('ranking_score');
  const [sortOrder, setSortOrder] = useState('0');
  const [paging, setPaging] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [interactionsFeed, setInteractionsFeed] = useState<CompactPaperProps[]>([]);
  const [featureToggles, setFeatureToggles] = useState<FeatureToggle[]>([]);
  const [allUserCollections, setAllUserCollections] = useState<string[]>([]);
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [paperElements, setPaperElements] = useState<HTMLDivElement[]>([]);
  const index = useRef<number>(0);
  const scrolling = useRef<boolean>(false);
  const referenceIndex = useRef<number>(0);
  const collapsedStatesRef = useRef<boolean[]>([]);
  const SWIPE_MARGIN = window.innerWidth < 458 ? 45 : 0;
  const FILTERBAR_HEIGHT = filterBarHeight ?? (SWIPE_MARGIN + 129);

  const handleDataFeedUpdate = (response) => {
    // guard vs empty response
    if (response == undefined || Object.keys(response).length === 0) {
      console.log(response);
      return;
    }
    console.log(response);

    // build digest data
    const digest: PaperProps[] = responseSetPaperProps(response);
    setInteractionsFeed(digest);
    setHasNextPage(response.has_next);
  };

  const handleSortParamChange = (event: SelectChangeEvent) => {
    setSortParam(event.target.value);
    setPaging(1);

    const interactionsData = GetInteractionsData(event.target.value, showParam, sortOrder);
    interactionsData.then(res => {
      if (res.success) {
        handleDataFeedUpdate(res);
      } else {
        if (res.next_step === 'bootstrapping') {
          // redirect
          navigate({
            pathname: '/onboarding/registration/',
          });
        }
      }
    });
  };


  const handleSortOrderChange = (event: SelectChangeEvent) => {
    setSortOrder(event.target.value);
    setPaging(1);

    const interactionsData = GetInteractionsData(sortParam, showParam, event.target.value);
    interactionsData.then(res => {
      if (res.success) {
        handleDataFeedUpdate(res);
      } else {
        if (res.next_step === 'bootstrapping') {
          // redirect
          navigate({
            pathname: '/onboarding/registration/',
          });
        }
      }
    });
  };


  const handleShowParamChange = (event: SelectChangeEvent) => {
    setShowParam(event.target.value);
    setPaging(1);

    const interactionsData = GetInteractionsData(sortParam, event.target.value, sortOrder);
    interactionsData.then(res => {
      if (res.success) {
        handleDataFeedUpdate(res);
      } else {
        if (res.next_step === 'bootstrapping') {
          // redirect
          navigate({
            pathname: '/onboarding/registration/',
          });
        }
      }
    });
  };


  const fetchNextPage = () => {
    console.log('fetching next');

    const interactionsData = GetInteractionsData(sortParam, showParam, sortOrder, paging);
    interactionsData.then(response => {
      if (response.success) {
        let digest = responseSetPaperProps(response);
        console.log(digest);
        setInteractionsFeed(interactionsFeed.concat(digest));
        setHasNextPage(response.has_next);
        setPaging(paging + 1);
      } else {
        if (response.next_step === 'bootstrapping') {
          // redirect
          navigate({
            pathname: '/onboarding/registration/',
          });
        }
      }
    });

    console.log('done next');
  };

  const handleCollapseToggle = (paperId: number) => {
    const paperIndex = interactionsFeed.findIndex(paper => paper.paperId === paperId);
    if (paperIndex === -1) return;
    collapsedStatesRef.current[paperIndex] = !collapsedStatesRef.current[paperIndex];
    if (collapsedStatesRef.current.every(state => state)) {
      setAllCollapsed(true);
    } else if (collapsedStatesRef.current.every(state => !state)) {
      setAllCollapsed(false);
    }
  };

  useEffect(() => {
    collapsedStatesRef.current = interactionsFeed.map(() => allCollapsed);
  }, [interactionsFeed, allCollapsed]);

  useEffect(() => {
    setPaperElements(Array.from(document.querySelectorAll('div[id^="paper"]')) as HTMLDivElement[]);
  }, [interactionsFeed]);
  
  // check which paper card is in the viewport after manually scrolling
  useEffect(() => {
    let timer = null;
    const debouncedScroll = () => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        for (let i = 0; i < paperElements.length; i++) {
          const rect = paperElements[i].getBoundingClientRect();
          if (rect.bottom >= FILTERBAR_HEIGHT) {
            index.current = i;
            scrolling.current = false;
            break;
          }
        }
      }, 180);
    };
    window.addEventListener('scroll', debouncedScroll);
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      if (timer !== null) clearTimeout(timer);
    };
  }, [paperElements, allCollapsed]);

  const handleSwipe = useCallback(({ deltaX, deltaY }) => {
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX > 0) {
          jumpUp(index, paperElements, FILTERBAR_HEIGHT, scrolling, referenceIndex);
        } else {
          if (index.current < paperElements.length - 1) {
            jumpDown(index, paperElements, FILTERBAR_HEIGHT, scrolling, referenceIndex);
          }
        }
    }
  }, [paperElements]);

  // load initial digest data
  useEffect(() => {
    const interactionsData = GetInteractionsData();
    const featureTogglesData = GetFeatureToggles();
    const allUserCollectionsRow = GetAllUserCollections();

    interactionsData.then(res => {
      if (res.success) {
        handleDataFeedUpdate(res);
      } else {
        if (res.next_step === 'bootstrapping') {
          // redirect
          navigate({
            pathname: '/onboarding/registration/',
          });
        }
      }
    });

    featureTogglesData.then(response => {
      if (response) {
        const data: FeatureToggle[] = extractFeatureToggles(response);
        setFeatureToggles(data);
      }
    });

    allUserCollectionsRow.then(response => {
      setAllUserCollections(response ? response : []);
    });
  }, []);


  return (
    <MobileSwiper onSwipe={handleSwipe}>
    <BoxDefaultContent fullWidth>
      {/* manager bar (includes Filterbar and JumpAndCollapsePanel) */}
      <Box sx = {{position: 'sticky', zIndex: 1, top: '64px', justifyContent: 'space-between', alignItems: 'stretch', display: 'flex' }}>
        <Box sx = {{
          ... filterBarStyle,
          marginTop: '0px',
        }}>
          <FilterBarShowParam
            handleChange={handleShowParamChange}
            label='Show option'
            optionList={[
              ['all', 'All rated'],
              ['read', 'All read'],
              ['positive', 'Positively rated'],
              ['negative', 'Negatively rated'],
            ]}
            value={showParam}
          />
          <FilterBarSortParam
            handleChange={handleSortParamChange}
            label='Sort option'
            optionList={[
              ['publication_date', 'Date'],
              ['ranking_score', 'Relevance'],
            ]}
            value={sortParam}
          />
          <FilterBarSortParam
            handleChange={handleSortOrderChange}
            label='Sort order'
            optionList={[
              ['0', 'Descending'],
              ['1', 'Ascending'],
            ]}
            value={sortOrder}
          />
        </Box>
        <Box sx={{height: '65px',  display: 'flex'}}>
          <JumpAndCollapsePanel
            index = {index}
            paperElements = {paperElements}
            allCollapsed = {allCollapsed}
            setAllCollapsed = {setAllCollapsed}
            scrolling = {scrolling}
            referenceIndex = {referenceIndex}
            margin = {FILTERBAR_HEIGHT}
          />
        </Box>
      </Box>

      <InfiniteScroll
        dataLength={interactionsFeed.length}
        next={fetchNextPage}
        hasMore={hasNextPage}
        style={{ overflow: 'visible', minHeight: 'calc(100vh - 128px)'}}
        loader={
          <Box sx={{ justifyContent: 'center', display: 'flex', height: '40px', mt: 5 }}>
            <TripleDotLoadingProgress />
          </Box>
        }
      >
        {interactionsFeed.map(paper => (
          <CompactPaper {...paper}
            key={paper.title}
            featureToggles={featureToggles}
            all_user_collections={allUserCollections}
            showAbstract = {!allCollapsed}
            onCollapseToggle = {handleCollapseToggle}
          />
        ))}
      </InfiniteScroll>

    </BoxDefaultContent>
    </MobileSwiper>
  )
}
